import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import SubpageHeader from "../components/SubpageHeader";
import CompactGrid from "../components/CompactGrid";
import MonthRecommendationRail from "../components/MonthRecommendationRail";
import SiteFooter from "../components/SiteFooter";
import BrowsePageIntro from "../components/BrowsePageIntro";
import Button from "../components/ui/Button";
import Dropdown from "../components/ui/Dropdown";
import Typography from "../components/ui/Typography";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorAlert from "../components/ErrorAlert";
import useWatchlist from "../hooks/useWatchlist";
import usePageMeta from "../hooks/usePageMeta";
import { formatDisplayDate } from "../utils/formatDisplayDate";
import { fetchMonthReleases } from "../services/backendApi";
import { handleInternalLinkClick } from "../utils/navigation";
import { resolveInitialPayload } from "../utils/initialPayload";
import { buildAggregateRatingSchema } from "../utils/showRatings";
import { buildMonthSeoMeta } from "./month/monthSeo";
import {
  DEFAULT_MONTH_TOOLS,
  MONTH_FILTERS,
  MONTH_SORTS,
  applyMonthTools,
  describeEmptyMonth,
} from "./month/monthTools";
import editorialSummaries from "../../data/editorial-summaries.json";

function getStructuredDataImage(show) {
  return show?.posterMetadata?.thumbnail || show?.poster || show?.omdbData?.poster || null;
}

function getStructuredDataDescription(show) {
  return show?.plotOverview || show?.summary || show?.omdbData?.plot || "";
}

const MonthStructuredData = ({ yearMonth, label, shows }) => {
  if (!shows || shows.length === 0) return null;

  const itemListJson = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `New TV Shows Premiering ${label}`,
    description: `Curated list of TV show premieres for ${label}`,
    url: `https://premierelens.com/month/${yearMonth}`,
    numberOfItems: shows.length,
    itemListElement: shows.slice(0, 20).map((show, index) => {
      const aggregateRating = buildAggregateRatingSchema(show);
      const image = getStructuredDataImage(show);
      const description = getStructuredDataDescription(show);

      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "TVSeries",
          name: show.title,
          url: `https://premierelens.com/show/${show.slug}`,
          ...(image ? { image } : {}),
          ...(description ? { description } : {}),
          ...(show.releaseDate ? { datePublished: show.releaseDate } : {}),
          ...(aggregateRating ? { aggregateRating } : {}),
        },
      };
    }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJson) }}
    />
  );
};

MonthStructuredData.propTypes = {
  yearMonth: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  shows: PropTypes.arrayOf(PropTypes.object),
};

const MonthPager = ({ label, previousYearMonth, nextYearMonth, previousLabel, nextLabel }) => {
  if (!previousYearMonth && !nextYearMonth) return null;
  return (
    <nav className="month-pager" aria-label={label}>
      {previousYearMonth && (
        <a
          href={`/month/${previousYearMonth}`}
          onClick={(event) => handleInternalLinkClick(event, `/month/${previousYearMonth}`)}
          className="month-pager__link"
        >
          <span>Previous</span>
          <strong>{previousLabel}</strong>
        </a>
      )}
      {nextYearMonth && (
        <a
          href={`/month/${nextYearMonth}`}
          onClick={(event) => handleInternalLinkClick(event, `/month/${nextYearMonth}`)}
          className="month-pager__link"
        >
          <span>Next</span>
          <strong>{nextLabel}</strong>
        </a>
      )}
    </nav>
  );
};

MonthPager.propTypes = {
  label: PropTypes.string.isRequired,
  previousYearMonth: PropTypes.string,
  nextYearMonth: PropTypes.string,
  previousLabel: PropTypes.string,
  nextLabel: PropTypes.string,
};

const MonthPage = ({ yearMonth, initialMonthPayload = null }) => {
  const initialPayload = useMemo(
    () => resolveInitialPayload(initialMonthPayload, "yearMonth", yearMonth),
    [yearMonth, initialMonthPayload]
  );

  const [payload, setPayload] = useState(() => initialPayload || null);
  const [loading, setLoading] = useState(() => !initialPayload);
  const [error, setError] = useState("");
  const [tools, setTools] = useState(DEFAULT_MONTH_TOOLS);
  const { isInWatchlist, toggleWatchlist, upsertWatchlistEntries } = useWatchlist();
  const baseSeo = useMemo(() => buildMonthSeoMeta(yearMonth), [yearMonth]);
  const titleMonth = payload?.label || baseSeo.label;

  useEffect(() => {
    if (initialPayload) return;
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        const nextPayload = await fetchMonthReleases(yearMonth);
        if (!active) return;
        setPayload(nextPayload);
        setError("");
      } catch (nextError) {
        if (!active) return;
        setError(nextError.message || "Failed to load monthly releases");
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [yearMonth, initialPayload]);

  const editorialSummary = payload?.editorialSummary || editorialSummaries?.month?.[yearMonth] || "";
  const metaDescription = (() => {
    const s = editorialSummary;
    if (!s) return baseSeo.description;
    return s.length <= 155 ? s : s.slice(0, 155).replace(/\s\S*$/, "") + "…";
  })();

  usePageMeta({
    title: baseSeo.title,
    description: metaDescription,
    pathname: `/month/${yearMonth}`,
    ogType: "website",
  });

  const shows = useMemo(() => (Array.isArray(payload?.data) ? payload.data : []), [payload?.data]);
  const ifYouLikedPairings = Array.isArray(payload?.ifYouLikedPairings) ? payload.ifYouLikedPairings : [];
  const previousYearMonth = payload?.previousYearMonth || null;
  const nextYearMonth = payload?.nextYearMonth || null;
  const previousLabel = previousYearMonth ? buildMonthSeoMeta(previousYearMonth).label : null;
  const nextLabel = nextYearMonth ? buildMonthSeoMeta(nextYearMonth).label : null;
  const updatedLabel = formatDisplayDate(payload?.lastUpdated) || null;

  const visibleShows = useMemo(() => applyMonthTools(shows, tools), [shows, tools]);
  const isDefaultTools = tools.filter === DEFAULT_MONTH_TOOLS.filter && tools.sort === DEFAULT_MONTH_TOOLS.sort;
  const resetTools = () => setTools(DEFAULT_MONTH_TOOLS);
  const pagerProps = { previousYearMonth, nextYearMonth, previousLabel, nextLabel };

  useEffect(() => {
    upsertWatchlistEntries(shows);
  }, [shows, upsertWatchlistEntries]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SubpageHeader backHref="/" backLabel="Home" />
      <MonthStructuredData yearMonth={yearMonth} label={titleMonth} shows={shows} />
      <main id="main-content" tabIndex={-1} className="route-shell route-shell--browse">
        <BrowsePageIntro
          id="month-page-title"
          title={`${titleMonth} premieres`}
          description={editorialSummary || `New scripted shows and returning seasons for ${titleMonth}.`}
          actions={(
            <>
              <Button variant="default" asChild>
                <a
                  href={`/best-of/${yearMonth}`}
                  onClick={(event) => handleInternalLinkClick(event, `/best-of/${yearMonth}`)}
                >
                  Best of {titleMonth}
                </a>
              </Button>
              <Button variant="ghost" className="browse-page-intro__quiet-action" asChild>
                <a href="/archive" onClick={(event) => handleInternalLinkClick(event, "/archive")}>
                  Browse Archive
                </a>
              </Button>
            </>
          )}
        >
          {updatedLabel && (
            <Typography variant="body-small" className="text-muted-foreground">
              Updated {updatedLabel}
            </Typography>
          )}
          <MonthPager label="Month archive navigation" {...pagerProps} />
        </BrowsePageIntro>

        <section aria-labelledby="month-premieres-heading" className="month-premieres">
          <Typography id="month-premieres-heading" as="h2" variant="h3" aria-live="polite">
            {loading || error
              ? `Premieres in ${titleMonth}`
              : visibleShows.length === shows.length
              ? `${shows.length} ${shows.length === 1 ? "premiere" : "premieres"} in ${titleMonth}`
              : `Showing ${visibleShows.length} of ${shows.length} premieres in ${titleMonth}`}
          </Typography>

          {shows.length > 1 && (
            <div className="month-tools">
              {[
                { key: "filter", label: "Show", options: MONTH_FILTERS },
                { key: "sort", label: "Sort by", options: MONTH_SORTS },
              ].map(({ key, label, options }) => (
                <div key={key} role="group" aria-labelledby={`month-tools-${key}`} className="month-tools__field">
                  <span id={`month-tools-${key}`} className="text-body-small font-semibold text-muted-foreground">{label}</span>
                  <Dropdown
                    value={[options.find((option) => option.value === tools[key]).label]}
                    options={options.map((option) => option.label)}
                    onChange={([picked]) => {
                      const next = options.find((option) => option.label === picked);
                      if (next) setTools((current) => ({ ...current, [key]: next.value }));
                    }}
                  />
                </div>
              ))}
              {!isDefaultTools && (
                <Button size="sm" variant="link" onClick={resetTools}>
                  Reset
                </Button>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="max-w-2xl mx-auto">
              <ErrorAlert message={error} />
            </div>
          ) : visibleShows.length === 0 ? (
            <p className="month-premieres__empty text-body text-muted-foreground">
              {describeEmptyMonth(tools.filter, titleMonth)}
              {!isDefaultTools && (
                <>
                  {" "}
                  <Button variant="link" className="month-premieres__show-all" onClick={resetTools}>
                    Show all premieres
                  </Button>
                </>
              )}
            </p>
          ) : (
            <CompactGrid
              releases={visibleShows}
              showTitle={false}
              isInWatchlist={isInWatchlist}
              onToggleWatchlist={toggleWatchlist}
            />
          )}
        </section>

        <MonthRecommendationRail pairings={ifYouLikedPairings} />

        <div className="month-end">
          <MonthPager label="More months" {...pagerProps} />
          <Button variant="ghost" className="browse-page-intro__quiet-action" asChild>
            <a
              href={`/best-of/${yearMonth}`}
              onClick={(event) => handleInternalLinkClick(event, `/best-of/${yearMonth}`)}
            >
              See the best of {titleMonth}
            </a>
          </Button>
        </div>

      </main>
      <SiteFooter />
    </div>
  );
};

MonthPage.propTypes = {
  yearMonth: PropTypes.string.isRequired,
  initialMonthPayload: PropTypes.shape({
    yearMonth: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    ifYouLikedPairings: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    previousYearMonth: PropTypes.string,
    nextYearMonth: PropTypes.string,
    totalCount: PropTypes.number,
    candidateCount: PropTypes.number,
    lastUpdated: PropTypes.string,
  }),
};

export default MonthPage;
